import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_ENDPOINTS as API_ENDPOINTS_V2, buildEndpoint as buildEndpointV2 } from '../api-endpoints-v2';
import { ApiResponse } from '../models/api-response';
import {
  FileProvisioningValidationResult,
  FileUploadMetadata,
  FileUploadResult,
  FileValidationMetadata,
  SupportingDocument,
  SupportingDocumentSequenceItem,
  SupportingDocumentSequenceResult,
} from '../models/provisioning';
import { RuntimeConfigService } from '../config/runtime-config.service';

/**
 * File provisioning service. Uploads go through the authed .NET API (proxy precedent —
 * mirrors how report download/search already flow through ReportService, inheriting the
 * Azure AD bearer + APIM auth attached by AuthInterceptor). RW File Provisioning §10.1.
 */
@Injectable({
  providedIn: 'root',
})
export class ProvisioningService {
  // V2 uses the same base API URL as v1; only the version/path differs.
  private baseApiV2Url: string;
  private apiVersion: string;

  constructor(
    private runtimeConfigService: RuntimeConfigService,
    private http: HttpClient
  ) {
    this.baseApiV2Url = this.runtimeConfigService.apiUrl;
    this.apiVersion = this.runtimeConfigService.apiVersion;
  }

  /**
   * Validate a drop session without storing anything (POST /api/v2/report-files/validate).
   * The server runs the deterministic checks (magic bytes, corruption, password protection,
   * size/count/aggregate limits — story 1.5) and the non-blocking ones (category mismatch,
   * missing References kind, Excel structural check — stories 3.1/3.2). Semantics:
   * - HTTP 200: everything acceptable; individual files may carry validationStatus=Warning.
   * - HTTP 400: at least one hard failure — the per-file breakdown is in the error
   *   envelope's `errors` field (HttpErrorResponse.error.errors), store nothing.
   * metadata is matched to files by fileName; category enum here is the validate API's
   * (DataFiles | References | Templates).
   */
  validateFiles(
    files: File[],
    metadata: FileValidationMetadata[]
  ): Observable<ApiResponse<FileProvisioningValidationResult>> {
    const url = buildEndpointV2(
      this.baseApiV2Url,
      this.apiVersion,
      'reportservice',
      API_ENDPOINTS_V2.REPORT_SERVICE.FILE_PROVISIONING.Validate
    );
    const form = new FormData();
    for (const f of files) {
      form.append('files', f, f.name);
    }
    if (metadata.length) {
      form.append('metadata', JSON.stringify(metadata));
    }
    return this.http.post<ApiResponse<FileProvisioningValidationResult>>(url, form);
  }

  /**
   * Upload a whole staged session in one multipart call (POST /api/v2/report-files/upload).
   * The server re-runs the deterministic validate checks, then for each file blobs it, persists
   * a record, and queues an ingestion message — files succeed or fail independently. `observe:
   * 'response'` keeps the status line so the caller can tell 201 (all stored) from 200 (partial:
   * at least one file failed — inspect data.items) apart. On HTTP 400 nothing is stored and the
   * per-file validation breakdown arrives in the error envelope's `errors`
   * (FileProvisioningValidationResult, same as /validate). metadata is matched to files by
   * fileName; every file needs a documentType or the request 400s.
   */
  uploadFiles(
    params: {
      projectId: number | string;
      client: string;
      metadata: FileUploadMetadata[];
      site?: string | null;
      /** Groups every upload request belonging to one generation run. Stable across Upload
       *  clicks — unlike correlationId, which identifies a single request. */
      uploadId: string;
      /** Only known after a prior upload in this run returned one; omitted on the first batch. */
      reportId?: string | null;
      /** Traces this one upload end-to-end. Sent as X-Correlation-ID, which AuthInterceptor
       *  passes through instead of substituting the per-tab session id, so a failed batch can be
       *  pinned in the server logs. Generate one per Upload click and keep it for the error path. */
      correlationId?: string;
    },
    files: File[]
  ): Observable<HttpResponse<ApiResponse<FileUploadResult>>> {
    const url = buildEndpointV2(
      this.baseApiV2Url,
      this.apiVersion,
      'reportservice',
      API_ENDPOINTS_V2.REPORT_SERVICE.FILE_PROVISIONING.Upload
    );
    const form = new FormData();
    for (const f of files) {
      form.append('files', f, f.name);
    }
    form.append('uploadId', params.uploadId);
    form.append('client', params.client);
    form.append('projectId', String(params.projectId));
    // reportId only exists once a prior batch in this run has been stored — same != null / ''
    // guard as site, so an unset one never reaches the server as "null"/"undefined".
    if (params.reportId != null && params.reportId !== '') {
      form.append('reportId', String(params.reportId));
    }
    // Optional site: only sent when supplied, and guarded on != null / '' (not truthiness) so a
    // falsy-but-valid value isn't dropped and an unset one never reaches the server as "undefined".
    if (params.site != null && params.site !== '') {
      form.append('site', String(params.site));
    }
    form.append('metadata', JSON.stringify(params.metadata));
    // observe the full response so a 200 (partial) can be told from a 201 (all stored).
    return this.http.post<ApiResponse<FileUploadResult>>(url, form, {
      observe: 'response',
      headers: params.correlationId ? { 'X-Correlation-ID': params.correlationId } : undefined,
    });
  }

  /**
   * Appendices held against a report, for the compile & export ordering list (US-17-01).
   * Sorted here so the caller always sees assembly order: documents without a sequence yet
   * fall to the end, in the order the server returned them.
   */
  getSupportingDocuments(reportId: number): Observable<SupportingDocument[]> {
    const url = buildEndpointV2(
      this.baseApiV2Url,
      this.apiVersion,
      'reportservice',
      API_ENDPOINTS_V2.REPORT_SERVICE.FILE_PROVISIONING.SupportingDocuments.replace(
        '{reportId}',
        String(reportId)
      )
    );
    return this.http.get<ApiResponse<SupportingDocument[]>>(url).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error(response.message);
        }
        return [...(response.data ?? [])].sort(bySequence);
      })
    );
  }

  /** Persist a reordering of the appendices (US-17-01). */
  updateSupportingDocumentSequence(
    reportId: number,
    items: SupportingDocumentSequenceItem[]
  ): Observable<SupportingDocumentSequenceResult> {
    const url = buildEndpointV2(
      this.baseApiV2Url,
      this.apiVersion,
      'reportservice',
      API_ENDPOINTS_V2.REPORT_SERVICE.FILE_PROVISIONING.SupportingDocumentSequence.replace(
        '{reportId}',
        String(reportId)
      )
    );
    return this.http
      .put<ApiResponse<SupportingDocumentSequenceResult>>(url, { items })
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new Error(response.message);
          }
          return response.data;
        })
      );
  }
}

/** Unsequenced documents sort last rather than jumping to the front as a 0 would. */
function bySequence(a: SupportingDocument, b: SupportingDocument): number {
  const left = a.sequence ?? Number.MAX_SAFE_INTEGER;
  const right = b.sequence ?? Number.MAX_SAFE_INTEGER;
  return left - right;
}
